import { environment } from '../environment';

/**
 * Base API Client
 * Handles all HTTP communication with Express backend
 * This is a simplified version for public access (no authentication required)
 */
class ApiClient {
  get apiUrl() {
    return environment.apiUrl;
  }

  get filesUrl() {
    return environment.filesUrl;
  }

  /**
   * Handle HTTP errors
   */
  handleError(error) {
    let errorMessage = 'An error occurred';

    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (error?.error?.message) {
      errorMessage = error.error.message;
    }

    console.error('API Error:', {
      message: errorMessage,
      error,
    });

    throw new Error(errorMessage);
  }

  /**
   * Generic GET request
   */
  async get(endpoint) {
    try {
      const response = await fetch(`${this.apiUrl}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const apiResponse = await response.json();

      if (!apiResponse.success) {
        throw new Error(apiResponse.error?.message || 'Request failed');
      }

      return apiResponse.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Generic POST request
   */
  async post(endpoint, data) {
    try {
      const response = await fetch(`${this.apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const apiResponse = await response.json();

      if (!apiResponse.success) {
        throw new Error(apiResponse.error?.message || 'Request failed');
      }

      return apiResponse.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Get file URL (for images, etc.)
   */
  getFileUrl(path) {
    return `${this.filesUrl}/${path}`;
  }

  /**
   * Build QR code image URL
   */
  getQrCodeImageUrl(uid, qrCodeId) {
    return `${this.filesUrl}/users/${uid}/qrcodes/${qrCodeId}.png`;
  }

  /**
   * Build card logo URL
   */
  getCardLogoUrl(uid, cardId) {
    return `${this.filesUrl}/users/${uid}/cards/${cardId}/logo-card.jpg`;
  }

  /**
   * Check if backend is reachable
   */
  async healthCheck() {
    try {
      const response = await fetch(`${this.apiUrl.replace('/api', '')}/health`);
      return response.ok;
    } catch (error) {
      console.error('Backend health check failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
